import  { HTMLAttributes, useState } from "react"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../common/alert-dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import useTranslator from "@pma/hooks/use-translator"
import { useMutation } from "@tanstack/react-query"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { DictType } from "@pma/types"
import axios from "axios"
import { FINANCIAL_SERVER_URL, RESTORE_MAIN_BOQ_ENDPOINT } from "@pma/utils/api-routes"
import { useAuth } from "@pma/stores/auth-store"
import { toast } from "@pma/hooks/use-toast"
import { Button } from "../common/button"

interface Props extends HTMLAttributes<HTMLDivElement> {
  boq: DictType
  refetch: () => {}
}

const RestoreMainBOQ = ({ boq, children, className, refetch }: Props) => {
  const { authConfig } = useAuth()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const { translate } = useTranslator()
  const [open, setOpen] = useState(false)

  const restoreMainBOQ = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + RESTORE_MAIN_BOQ_ENDPOINT,
      {
        organization_id: authConfig.last_organization_id,
        boqs_id: [boq?.id],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const restoreMainBOQMutation = useMutation({
    mutationKey: ["restore-main-boq", boq?.id],
    mutationFn: restoreMainBOQ,
    onSuccess: (data) => {
      refetch()
      toast({
        title: translate("Success"),
        description: `${boq?.name} ${translate("has been restored")}`,
        variant: "success",
      })
      setOpen(false)
    },
    onError: (error: any) => {
      setOpen(false)
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage ||
          `${translate("Unable to restored")} ${boq?.number} ${translate("Main BOQ")}`,
        variant: "error",
      })
    },
  })

  return (
    <AlertDialog open={open}>
      <AlertDialogTrigger className={className} onClick={() => setOpen(true)}>
        <Tooltip>
          <TooltipTrigger>{children}</TooltipTrigger>
          <TooltipContent className='flex items-center gap-1'>
            <h4>
              {translate(`Restore`)} {boq?.name}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to restore")} {boq?.name}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This will restore the main BOQ")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setOpen(false)}>
            {translate("Cancel")}
          </AlertDialogCancel>
          <Button
            disabled={restoreMainBOQMutation?.isPending}
            loading={restoreMainBOQMutation?.isPending}
            onClick={() => restoreMainBOQMutation.mutate()}
          >
            {translate("Continue")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default RestoreMainBOQ
